import useNaicsSectors from "../../../shared/hooks/useNaicsSectors";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../services/api/axios";
import StateDropdown from "../../../shared/forms/StateProvinceDropDown";
import CountryDropdown from "../../../shared/forms/CountriesDropDown";
import CountyDropdown from "../../../shared/forms/CountyDropDown";
import { useAuth } from "../../../providers/AuthProvider";
import { addRecentItem } from "../../home-dashboard/RecentItemsHelper";
import ProspectingContacts from "../contacts/ProspectingContacts";
import BackButton from "../../../shared/components/BackButton";
import CompanyActivityTracker from "../../../shared/components/CompanyActivityTracker";
import ProspectingCompanyWebpagesVisitedTab from "./ProspectingCompanyWebpagesVisitedTab";
import ProspectingCompanyProfileDocumentsTab from "./ProspectingCompanyProfileDocumentsTab";
import ProspectingCompanyContactsModal from "./ProspectingCompanyContactsModal";
import { PRIMARY_BUTTON_SX } from "../../../theme/buttonstyles";
import { lookupProspectingCompanyMatches } from "./ProspectingCompanyAPI";
import SectorDropdown from "../../../shared/forms/SectorDropDown";
import {
  formatBroadSectorLabel,
} from "../../bre/companies/BRECompanyHelpers";

import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Tabs,
  Tab,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
  Paper,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import StorageIcon from "@mui/icons-material/Storage";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import InputAdornment from "@mui/material/InputAdornment";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

const PIPELINE_STAGE_OPTIONS = [
  "New - Needs Outreach",
  "Outreach Sent",
  "In Conversation",
  "Qualified Opportunity",
  "Responded to RFI",
  "Closed Won",
  "Closed Lost",
  "Nurture / Not Now",
];

const PIPELINE_STAGE_CONFIG = {
  "New - Needs Outreach":   { bg: "#FFF8E1", color: "#E65100", border: "#FFB300" },
  "Outreach Sent":          { bg: "#E0F2FE", color: "#0369A1", border: "#0284C7" },
  "In Conversation":        { bg: "#E3F2FD", color: "#0D47A1", border: "#1976D2" },
  "Qualified Opportunity":  { bg: "#EDE9FE", color: "#5B21B6", border: "#7C3AED" },
  "Responded to RFI":       { bg: "#E8F5E9", color: "#2E7D32", border: "#43A047" },
  "Closed Won":             { bg: "#ECFDF5", color: "#047857", border: "#10B981" },
  "Closed Lost":            { bg: "#FEE2E2", color: "#B91C1C", border: "#EF4444" },
  "Nurture / Not Now":      { bg: "#F1F5F9", color: "#475569", border: "#94A3B8" },
};

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return String(value ?? "").trim().length > 0;
};

const getCandidateSummary = (candidate) => [
  [candidate.city, candidate.state_or_province].filter(Boolean).join(", "),
  candidate.country,
  candidate.sector,
  candidate.industry_or_sector &&
  candidate.industry_or_sector.toLowerCase() !== String(candidate.sector || "").toLowerCase()
    ? candidate.industry_or_sector
    : "",
  candidate.employee_count ? `${candidate.employee_count} employees` : "",
].filter(Boolean);

const getVisibleMatchReasons = (candidate) =>
  (candidate.match_reasons || []).filter(
    (reason) =>
      reason !== "Matched by Seamless company search" &&
      reason !== "Exact company-name match from Seamless",
  );

const buildCandidatePatch = (candidate, sectorOptions) => {
  const patch = {
    name: candidate.company_name || "",
    website: candidate.website || "",
    description: candidate.description || "",
    phone_number: candidate.phone || "",
    address: candidate.address || "",
    city: candidate.city || "",
    state: candidate.state_or_province || "",
    zip_code: candidate.zip_code || "",
    country: candidate.country || "",
    linkedin: candidate.linkedin || "",
    twitter: candidate.twitter || "",
    facebook: candidate.facebook || "",
    number_of_employees:
      candidate.employee_count != null ? String(candidate.employee_count) : "",
  };

  const sector = candidate.sector || "";
  const industryOrSector = candidate.industry_or_sector || "";
  const sectorMatch = sectorOptions.find(
    (option) => option.toLowerCase() === (sector || industryOrSector).toLowerCase(),
  );

  if (sectorMatch) {
    patch.sector = sectorMatch;
  }

  if (industryOrSector) {
    patch.naics_name = industryOrSector;
  }

  if (candidate.naics_code) {
    patch.naics_code = candidate.naics_code;
    patch.naics_name = candidate.naics_name || industryOrSector;
    patch.naics_extra = candidate.naics_extra;
  }

  return patch;
};

const getSectionCompletion = (company, fields) => {
  const completed = fields.filter((field) =>
    hasValue(field.getValue ? field.getValue(company) : company[field.key]),
  ).length;
  return { completed, total: fields.length };
};

function SectionHeader({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
      <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: BRAND_GREEN, flexShrink: 0 }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE }}>
        {children}
      </Typography>
    </Box>
  );
}

function ProspectingCompanyDetails() {
  const { options: sectorOptions } = useNaicsSectors();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = window.location.pathname.endsWith("/new");
  const safeString = (v) => (typeof v === "string" ? v : "");

  const [company, setCompany] = useState({
    dynamics_account_id: "", external_visit_id: "", domain: "", name: "",
    sector: "", naics_name: "", naics_code: "", phone_number: "", city: "",
    primary_naics_bucket: "", naics_buckets: [],
    county: "", zip_code: "", state: "", country: "", number_of_employees: "",
    industries_served: "", geographic_markets_covered: "", website: "",
    description: "", plan_for_change: "", location_drivers: "", time_frame: "",
    misc_lead_notes: "", site_use: "", new_facility_size: "", new_facility_needs: "",
    utility_requirements: "", transportation_requirements: "", number_of_initial_jobs: "",
    will_more_jobs_be_added: "", positions_required_skills: "", typical_pay_ranges: "",
    data_source: "", address: "", lead_score: null, pipeline_stage: "",
    added_to_external_db: false, profile_document_name: "",
    profile_document_uploaded_at: null, profile_documents: [],
    linkedin: "", twitter: "", facebook: "",
  });

  const [loading, setLoading] = useState(!isNew);
  const [tabIndex, setTabIndex] = useState(0);
  const [contactsModalOpen, setContactsModalOpen] = useState(false);
  const [contactsRefreshKey, setContactsRefreshKey] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [companyMatches, setCompanyMatches] = useState([]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMsg(message); setSnackbarSeverity(severity); setSnackbarOpen(true);
  };

  const profileCompletion = React.useMemo(() => {
    const sections = {
      general: getSectionCompletion(company, [
        { key: "name" },
        { key: "phone_number" },
        { key: "website" },
        { key: "description" },
      ]),
      location: getSectionCompletion(company, [
        { key: "country" },
        { key: "state" },
        { key: "city" },
        { key: "county" },
        { key: "address" },
        { key: "zip_code" },
      ]),
      business: getSectionCompletion(company, [
        { key: "sector" },
        { getValue: () => company.naics_name || company.naics_code },
        { key: "number_of_employees" },
        { key: "industries_served" },
        { key: "geographic_markets_covered" },
        { key: "data_source" },
      ]),
      social: getSectionCompletion(company, [
        { key: "linkedin" },
        { key: "twitter" },
        { key: "facebook" },
      ]),
    };
    const completed = Object.values(sections).reduce(
      (sum, section) => sum + section.completed,
      0,
    );
    const total = Object.values(sections).reduce(
      (sum, section) => sum + section.total,
      0,
    );

    return {
      ...sections,
      completed,
      total,
      overall: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [company]);

  const toNullIfBlank = (v) => (v === "" || v === undefined || v === null ? null : v);
  const asIntOrNull = (v) => {
    const n = parseInt(String(v ?? "").replace(/[, ]/g, ""), 10);
    return Number.isFinite(n) ? n : null;
  };

  useEffect(() => {
    if (isNew || !id) { setLoading(false); return; }
    setLoading(true);
    api.get(`/prospecting-companies/${id}`)
      .then((res) => {
        const data = res.data || {};
        setCompany({
          dynamics_account_id: data.dynamics_account_id || "",
          external_visit_id: data.external_visit_id || "",
          domain: data.domain || "",
          name: data.name || "",
          sector: data.sector || "",
          naics_name: data.naics_name || data.primary_naics_bucket || "",
          naics_code: data.naics_code || "",
          primary_naics_bucket: data.primary_naics_bucket || "",
          naics_buckets: Array.isArray(data.naics_buckets) ? data.naics_buckets : [],
          phone_number: data.phone_number || "",
          city: data.city || "",
          county: data.county || "",
          zip_code: data.zip_code || "",
          state: data.state || "",
          country: data.country || "",
          number_of_employees: data.number_of_employees != null ? String(data.number_of_employees) : "",
          industries_served: data.industries_served || "",
          geographic_markets_covered: data.geographic_markets_covered || "",
          website: data.website || "",
          description: data.description || "",
          plan_for_change: data.plan_for_change || "",
          location_drivers: data.location_drivers || "",
          time_frame: data.time_frame || "",
          misc_lead_notes: data.misc_lead_notes || "",
          site_use: data.site_use || "",
          new_facility_size: data.new_facility_size || "",
          new_facility_needs: data.new_facility_needs || "",
          utility_requirements: data.utility_requirements || "",
          transportation_requirements: data.transportation_requirements || "",
          number_of_initial_jobs: data.number_of_initial_jobs || "",
          will_more_jobs_be_added: data.will_more_jobs_be_added || "",
          positions_required_skills: data.positions_required_skills || "",
          typical_pay_ranges: data.typical_pay_ranges || "",
          data_source: data.data_source || "",
          address: data.address || "",
          lead_score: data.lead_score != null ? data.lead_score : null,
          pipeline_stage: data.pipeline_stage || "",
          added_to_external_db: Boolean(data.added_to_external_db),
          profile_document_name: data.profile_document_name || "",
          profile_document_uploaded_at: data.profile_document_uploaded_at ?? null,
          profile_documents: Array.isArray(data.profile_documents) ? data.profile_documents : [],
          linkedin: data.linkedin || "",
          twitter: data.twitter || "",
          facebook: data.facebook || "",
        });
      })
      .catch(() => showSnackbar("Failed to load prospect details.", "error"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  useEffect(() => {
    if (!user?.id || isNew || !id || !company?.name) return;
    addRecentItem(user.id, "prospect", { id: Number(id), name: company.name });
  }, [user?.id, id, isNew, company?.name]);

  const handleSectorChange = (e) => {
    setCompany((prev) => ({ ...prev, sector: e.target.value, naics_name: "", naics_code: "" }));
  };

  const handlePipelineStageChange = async (stage) => {
    try {
      if (stage === "Qualified Opportunity") {
        await api.post(`/prospects/${id}/mark-lead`);
      } else {
        await api.patch(`/prospecting-companies/${id}/pipeline-stage`, { pipeline_stage: stage === "" ? null : stage });
      }
      setCompany((prev) => ({ ...prev, pipeline_stage: stage }));
    } catch {
      showSnackbar("Failed to update prospect status.", "error");
    }
  };

  const handleChange = (field) => (e) => setCompany((prev) => ({ ...prev, [field]: e.target.value }));

  const handleFindCompanyMatches = () => {
    const companyName = company.name?.trim();
    const website = (company.website || company.domain || "").trim();

    if (!companyName && !website) {
      showSnackbar("Enter a company name or website before finding matches.", "warning");
      return;
    }

    setLookupLoading(true);
    setCompanyMatches([]);
    setMatchDialogOpen(true);

    lookupProspectingCompanyMatches({ companyName, website })
      .then((res) => {
        const matches = [
          ...(res.data?.final_matches || []),
          ...(res.data?.local_matches || []),
        ];
        setCompanyMatches(matches);
      })
      .catch((err) => {
        const message = err.response?.data?.detail || "Failed to find matching companies.";
        showSnackbar(Array.isArray(message) ? "Failed to find matching companies." : message, "error");
        setMatchDialogOpen(false);
      })
      .finally(() => setLookupLoading(false));
  };

  const handleApplyCompanyMatch = (candidate) => {
    const patch = buildCandidatePatch(candidate, sectorOptions);
    const fieldsToApply = Object.entries(patch).filter(
      ([field, value]) => hasValue(value) && !hasValue(company[field]),
    );

    setCompany((prev) => ({
      ...prev,
      ...Object.fromEntries(fieldsToApply),
    }));

    setMatchDialogOpen(false);
    showSnackbar(
      fieldsToApply.length > 0
        ? `Populated ${fieldsToApply.length} missing field${fieldsToApply.length === 1 ? "" : "s"}.`
        : "No empty fields were available to populate.",
      fieldsToApply.length > 0 ? "success" : "info",
    );
  };

  const handleStateChange = (e) => {
    const nextState = e.target.value;
    setCompany((prev) => ({ ...prev, state: nextState, county: nextState === prev.state ? prev.county : "" }));
  };

  const handleCountryChange = (e) => {
    const nextCountry = e.target.value;
    setCompany((prev) => ({ ...prev, country: nextCountry, county: nextCountry === prev.country ? prev.county : "" }));
  };

  const handleToggleExternalDB = async (checked) => {
    try {
      await api.patch(`/prospecting-companies/${id}`, { added_to_external_db: checked });
      setCompany((prev) => ({ ...prev, added_to_external_db: checked }));
    } catch {
      showSnackbar("Failed to update external database status.", "error");
    }
  };

  const buildPayload = () => {
    const src = { ...company };
    const cleaned = {
      name: toNullIfBlank(src.name), sector: toNullIfBlank(src.sector),
      naics_name: toNullIfBlank(src.naics_name), naics_code: toNullIfBlank(src.naics_code),
      phone_number: toNullIfBlank(src.phone_number), city: toNullIfBlank(src.city),
      county: toNullIfBlank(src.county), zip_code: toNullIfBlank(src.zip_code),
      state: toNullIfBlank(src.state), country: toNullIfBlank(src.country),
      number_of_employees: asIntOrNull(src.number_of_employees),
      industries_served: toNullIfBlank(src.industries_served),
      geographic_markets_covered: toNullIfBlank(src.geographic_markets_covered),
      website: toNullIfBlank(src.website), description: toNullIfBlank(src.description),
      plan_for_change: toNullIfBlank(src.plan_for_change), location_drivers: toNullIfBlank(src.location_drivers),
      time_frame: toNullIfBlank(src.time_frame), misc_lead_notes: toNullIfBlank(src.misc_lead_notes),
      site_use: toNullIfBlank(src.site_use), new_facility_size: toNullIfBlank(src.new_facility_size),
      new_facility_needs: toNullIfBlank(src.new_facility_needs),
      utility_requirements: toNullIfBlank(src.utility_requirements),
      transportation_requirements: toNullIfBlank(src.transportation_requirements),
      number_of_initial_jobs: toNullIfBlank(src.number_of_initial_jobs),
      will_more_jobs_be_added: toNullIfBlank(src.will_more_jobs_be_added),
      positions_required_skills: toNullIfBlank(src.positions_required_skills),
      typical_pay_ranges: toNullIfBlank(src.typical_pay_ranges),
      data_source: toNullIfBlank(src.data_source), address: toNullIfBlank(src.address),
      linkedin: toNullIfBlank(src.linkedin), twitter: toNullIfBlank(src.twitter), facebook: toNullIfBlank(src.facebook),
    };
    const allowedKeys = [
      "name","sector","naics_name","naics_code","phone_number","city","county","zip_code",
      "state","country","number_of_employees","industries_served","geographic_markets_covered",
      "website","description","plan_for_change","location_drivers","time_frame","misc_lead_notes",
      "site_use","new_facility_size","new_facility_needs","utility_requirements",
      "transportation_requirements","number_of_initial_jobs","will_more_jobs_be_added",
      "positions_required_skills","typical_pay_ranges","data_source","address",
      "linkedin","twitter","facebook",
    ];
    const payload = {};
    for (const key of allowedKeys) { if (cleaned[key] !== undefined) payload[key] = cleaned[key]; }
    return payload;
  };

  const handleSubmit = () => {
    const payload = buildPayload();
    const request = isNew
      ? api.post("/prospecting-companies", payload)
      : api.patch(`/prospecting-companies/${id}`, payload);
    request
      .then(() => navigate("/prospecting-companies"))
      .catch((err) => {
        const detail = err.response?.data?.detail;
        const message = Array.isArray(detail)
          ? detail.map((d) => { const loc = Array.isArray(d.loc) ? d.loc.join(".") : d.loc; return `${loc}: ${d.msg}`; }).join("\n")
          : typeof detail === "string" ? detail : "Invalid request";
        showSnackbar(message, "error");
      });
  };

  if (loading) return (
    <Box sx={{ p: 4, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <Typography sx={{ color: BRAND_BLUE, fontWeight: 600 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2, minHeight: "100%", boxSizing: "border-box", overflowX: "hidden", bgcolor: "#f0f4f8" }}>
      <Box sx={{ maxWidth: 1850, mx: "auto", width: "100%" }}>

        {/* HEADER CARD */}
        <Paper elevation={0} sx={{ mb: 2, borderRadius: 3, overflow: "hidden", border: "1px solid rgba(0,51,108,0.10)", boxShadow: "0 2px 12px rgba(0,51,108,0.08)", bgcolor: "white" }}>
          <Box sx={{ height: 4, background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_GREEN})` }} />

          <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
              <BackButton to="/prospecting-companies" />
              {user?.organization?.name && (
                <Chip label={user.organization.name} size="small" sx={{ bgcolor: "rgba(0,51,108,0.06)", color: BRAND_BLUE, fontWeight: 600, fontSize: 12, border: "1px solid rgba(0,51,108,0.12)" }} />
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 46, height: 46, borderRadius: 2, bgcolor: "rgba(0,51,108,0.07)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <BusinessIcon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ color: BRAND_BLUE, fontWeight: 700, lineHeight: 1.2 }}>
                  {isNew ? "New Prospect" : company.name || "Untitled Prospect"}
                </Typography>
                {!isNew && (company.city || company.state) && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                    <LocationOnIcon sx={{ fontSize: 14, color: "#d32f2f" }} />
                    <Typography variant="body2" sx={{ color: BRAND_BLUE }}>
                      {[company.city, company.state].filter(Boolean).join(", ")}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Pipeline stage chips + external DB */}
            {!isNew && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}>
                    Status
                  </Typography>
                  {PIPELINE_STAGE_OPTIONS.map((stage) => {
                    const cfg = PIPELINE_STAGE_CONFIG[stage];
                    const isActive = company.pipeline_stage === stage;
                    return (
                      <Chip
                        key={stage}
                        label={stage}
                        size="small"
                        onClick={() => handlePipelineStageChange(isActive ? "" : stage)}
                        sx={{
                          cursor: "pointer",
                          fontWeight: isActive ? 700 : 500,
                          fontSize: 12,
                          bgcolor: isActive ? cfg.bg : "transparent",
                          color: isActive ? cfg.color : "text.secondary",
                          border: `1.5px solid ${isActive ? cfg.border : "rgba(0,0,0,0.15)"}`,
                          transition: "all 0.15s ease",
                          "&:hover": { bgcolor: cfg.bg, color: cfg.color, border: `1.5px solid ${cfg.border}` },
                        }}
                      />
                    );
                  })}
                </Box>

                <Divider orientation="vertical" flexItem />

                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch checked={!!company.added_to_external_db} onChange={(e) => handleToggleExternalDB(e.target.checked)} color="success" size="small" />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <StorageIcon sx={{ fontSize: 15, color: company.added_to_external_db ? BRAND_GREEN : "text.secondary" }} />
                      <Typography sx={{ fontWeight: 600, color: company.added_to_external_db ? BRAND_GREEN : "text.secondary", fontSize: 13 }}>
                        External DB
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}
          </Box>

          {/* Tab bar */}
          <Box sx={{ px: 3, borderTop: "1px solid rgba(0,51,108,0.08)" }}>
            <Tabs
              value={tabIndex}
              onChange={(_e, i) => setTabIndex(i)}
              sx={{
                "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 14, minHeight: 44, color: "text.secondary", "&.Mui-selected": { color: BRAND_BLUE } },
                "& .MuiTabs-indicator": { backgroundColor: BRAND_GREEN, height: 3, borderRadius: "3px 3px 0 0" },
              }}
            >
              <Tab label="Company Details" />
              {!isNew && <Tab label="Activity" />}
              {!isNew && <Tab label="Contacts" />}
              {!isNew && <Tab label="Prospect Profile" />}
              {!isNew && <Tab label="Webpages Visited" />}
            </Tabs>
          </Box>
        </Paper>

        {/* TAB CONTENT CARD */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,51,108,0.10)", boxShadow: "0 2px 12px rgba(0,51,108,0.08)", p: 3, bgcolor: "white" }}>

          {/* COMPANY DETAILS TAB */}
          {tabIndex === 0 && (
            <Box>
              <Box
                sx={{
                  mb: 2.5,
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  border: "1px solid rgba(0,51,108,0.06)",
                  bgcolor: "rgba(0,51,108,0.025)",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 900, lineHeight: 1.15 }}>
                      Company Profile
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                      {profileCompletion.completed} of {profileCompletion.total} fields completed
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      startIcon={<AutoAwesomeIcon />}
                      onClick={handleFindCompanyMatches}
                      disabled={lookupLoading}
                      sx={{
                        borderColor: BRAND_BLUE,
                        color: BRAND_BLUE,
                        fontWeight: 800,
                        textTransform: "none",
                        "&:hover": {
                          borderColor: BRAND_BLUE,
                          bgcolor: "rgba(0,51,108,0.06)",
                        },
                      }}
                    >
                      Find Matches
                    </Button>
                    <Chip
                      label={`${profileCompletion.overall}% Complete`}
                      sx={{
                        bgcolor: "rgba(100,131,21,0.12)",
                        color: BRAND_GREEN,
                        border: "1px solid rgba(100,131,21,0.25)",
                        fontWeight: 900,
                      }}
                    />
                  </Stack>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={profileCompletion.overall}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    bgcolor: "rgba(0,51,108,0.10)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      bgcolor: BRAND_GREEN,
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>

              {/* General */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
                <SectionHeader>General</SectionHeader>
                <Stack spacing={3}>
                  <TextField label="Company Name" value={safeString(company.name)} onChange={handleChange("name")} fullWidth />
                  <TextField label="Website" value={safeString(company.website)} onChange={handleChange("website")} fullWidth />
                  <TextField label="Phone" value={safeString(company.phone_number)} onChange={handleChange("phone_number")} fullWidth />
                  <TextField
                    label="Description"
                    value={safeString(company.description)}
                    onChange={handleChange("description")}
                    fullWidth
                    multiline
                    rows={3}
                    sx={{ "& .MuiInputBase-inputMultiline": { height: "91px !important", overflowY: "auto", resize: "none" } }}
                  />
                  {!isNew && (
                    <TextField label="Lead Score" value={company.lead_score ?? "—"} fullWidth InputProps={{ readOnly: true }}
                      sx={{ "& .MuiInputBase-root": { bgcolor: "rgba(0,0,0,0.03)" } }}
                    />
                  )}
                </Stack>
              </Box>

              {/* Location */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
                <SectionHeader>Location</SectionHeader>
                <Stack spacing={3}>
                  <CountryDropdown label="Country" value={safeString(company.country)} onChange={handleCountryChange} fullWidth />
                  <StateDropdown label="State / Province" country={company.country} value={safeString(company.state)} onChange={handleStateChange} fullWidth />
                  <TextField label="City" value={safeString(company.city)} onChange={handleChange("city")} fullWidth />
                  {String(company.country || "").trim().toLowerCase() === "united states" && (
                    <CountyDropdown label="County" country={company.country} state={company.state} value={safeString(company.county)} onChange={handleChange("county")} />
                  )}
                  <TextField label="Address" value={safeString(company.address)} onChange={handleChange("address")} fullWidth />
                  <TextField label="Zip Code" value={safeString(company.zip_code)} onChange={handleChange("zip_code")} fullWidth />
                </Stack>
              </Box>

              {/* Business Details */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
                <SectionHeader>Business Details</SectionHeader>
                <Stack spacing={3}>
                  <SectorDropdown
                    label="Sector"
                    value={company.sector}
                    onChange={handleSectorChange}

                    formatOptionLabel={formatBroadSectorLabel}
                    sx={{
                      "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused, & .MuiInputBase-input, & .MuiSelect-icon": {
                        color: BRAND_BLUE,
                      },
                    }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: { "& .MuiMenuItem-root": { color: BRAND_BLUE } },
                        },
                      },
                    }}
                    fullWidth
                  />
                  <TextField label="NAICS Text" value={safeString(company.naics_name)} onChange={handleChange("naics_name")} fullWidth />
                  <TextField label="NAICS Code" value={safeString(company.naics_code)} onChange={handleChange("naics_code")} fullWidth />
                  <TextField label="Number of Employees" type="number" value={company.number_of_employees ?? ""} onChange={handleChange("number_of_employees")} fullWidth />
                  <TextField label="Industries Served" value={safeString(company.industries_served)} onChange={handleChange("industries_served")} fullWidth />
                  <TextField label="Geographic Markets Covered" value={safeString(company.geographic_markets_covered)} onChange={handleChange("geographic_markets_covered")} fullWidth />
                  <TextField label="Data Source" value={safeString(company.data_source)} onChange={handleChange("data_source")} fullWidth />
                </Stack>
              </Box>

              {/* Social Media */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(0,51,108,0.025)", border: "1px solid rgba(0,51,108,0.07)" }}>
                <SectionHeader>Social Media</SectionHeader>
                <Stack spacing={3}>
                  <TextField
                    label="LinkedIn"
                    value={safeString(company.linkedin)}
                    onChange={handleChange("linkedin")}
                    fullWidth
                    InputProps={{ startAdornment: <InputAdornment position="start"><LinkedInIcon sx={{ color: "#0A66C2", fontSize: 20 }} /></InputAdornment> }}
                  />
                  <TextField
                    label="Twitter / X"
                    value={safeString(company.twitter)}
                    onChange={handleChange("twitter")}
                    fullWidth
                    InputProps={{ startAdornment: <InputAdornment position="start"><TwitterIcon sx={{ color: "#1DA1F2", fontSize: 20 }} /></InputAdornment> }}
                  />
                  <TextField
                    label="Facebook"
                    value={safeString(company.facebook)}
                    onChange={handleChange("facebook")}
                    fullWidth
                    InputProps={{ startAdornment: <InputAdornment position="start"><FacebookIcon sx={{ color: "#1877F2", fontSize: 20 }} /></InputAdornment> }}
                  />
                </Stack>
              </Box>

            </Box>
            </Box>
          )}

          {/* ACTIVITY TAB */}
          {!isNew && tabIndex === 1 && (
            <Box sx={{ width: "100%" }}>
              <CompanyActivityTracker prospectId={Number(id)} maxHeight="75vh" />
            </Box>
          )}

          {/* CONTACTS TAB */}
          {!isNew && tabIndex === 2 && (
            <Stack spacing={2} sx={{ width: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="contained" startIcon={<SearchIcon />} onClick={() => setContactsModalOpen(true)} sx={PRIMARY_BUTTON_SX}>
                  Find Contacts
                </Button>
              </Box>
              <ProspectingContacts embedded prospectingCompanyId={id} refreshKey={contactsRefreshKey} />
            </Stack>
          )}

          {/* PROSPECT PROFILE TAB */}
          {!isNew && tabIndex === 3 && (
            <Box sx={{ width: "100%" }}>
              <ProspectingCompanyProfileDocumentsTab
                companyId={id}
                company={company}
                onFieldChange={handleChange}
                onSaveProfileDetails={handleSubmit}
                onCompanyUpdated={(nextCompany) =>
                  setCompany((prev) => ({
                    ...prev, ...nextCompany,
                    number_of_employees: nextCompany?.number_of_employees != null ? String(nextCompany.number_of_employees) : "",
                    lead_score: nextCompany?.lead_score != null ? nextCompany.lead_score : null,
                    added_to_external_db: Boolean(nextCompany?.added_to_external_db),
                    profile_document_name: nextCompany?.profile_document_name || "",
                    profile_document_uploaded_at: nextCompany?.profile_document_uploaded_at ?? null,
                    profile_documents: Array.isArray(nextCompany?.profile_documents) ? nextCompany.profile_documents : [],
                    utility_requirements: nextCompany?.utility_requirements || "",
                    transportation_requirements: nextCompany?.transportation_requirements || "",
                  }))
                }
              />
            </Box>
          )}

          {/* WEBPAGES VISITED TAB */}
          {!isNew && tabIndex === 4 && (
            <Box sx={{ width: "100%" }}>
              <ProspectingCompanyWebpagesVisitedTab company={company} />
            </Box>
          )}

          {/* SAVE BUTTON */}
          {tabIndex === 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, pt: 2.5, borderTop: "1px solid rgba(0,51,108,0.08)" }}>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} sx={PRIMARY_BUTTON_SX}>
                {isNew ? "Add Company" : "Save Changes"}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)} variant="filled">
          {snackbarMsg}
        </Alert>
      </Snackbar>

      <Dialog
        open={matchDialogOpen}
        onClose={() => setMatchDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: "1px solid rgba(0,51,108,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: BRAND_BLUE,
            fontWeight: 900,
            borderBottom: "1px solid rgba(0,51,108,0.10)",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <AutoAwesomeIcon sx={{ color: BRAND_GREEN }} />
          Matching Companies
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
          {lookupLoading ? (
            <Box sx={{ py: 7, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
              <CircularProgress size={24} sx={{ color: BRAND_BLUE }} />
              <Typography sx={{ color: BRAND_BLUE, fontWeight: 700 }}>Finding matches...</Typography>
            </Box>
          ) : companyMatches.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>No matching companies found.</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                Try a website domain or a more complete company name.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0} divider={<Divider />} sx={{ maxHeight: "62vh", overflowY: "auto" }}>
              {companyMatches.map((candidate) => {
                const summary = getCandidateSummary(candidate);
                const matchReasons = getVisibleMatchReasons(candidate);

                return (
                  <Box
                    key={candidate.candidate_id}
                    sx={{
                      p: 2.25,
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
                      gap: 2,
                      alignItems: "start",
                      "&:hover": { bgcolor: "rgba(0,51,108,0.025)" },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", mb: 0.75 }}>
                        <Typography sx={{ color: BRAND_BLUE, fontWeight: 900, fontSize: 16 }}>
                          {candidate.company_name || "Unnamed company"}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${candidate.confidence_score}% match`}
                          sx={{
                            bgcolor: "rgba(100,131,21,0.12)",
                            color: BRAND_GREEN,
                            border: "1px solid rgba(100,131,21,0.25)",
                            fontWeight: 800,
                          }}
                        />
                      </Stack>

                      {candidate.website && (
                        <Typography variant="body2" sx={{ color: BRAND_BLUE, mb: 0.5, overflowWrap: "anywhere" }}>
                          {candidate.website}
                        </Typography>
                      )}

                      {summary.length > 0 && (
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                          {summary.join(" | ")}
                        </Typography>
                      )}

                      {matchReasons.length > 0 && (
                        <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75 }}>
                          {matchReasons.slice(0, 3).map((reason) => (
                            <Chip
                              key={reason}
                              size="small"
                              label={reason}
                              sx={{ bgcolor: "#f5f7fa", color: "text.secondary", maxWidth: "100%" }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>

                    <Button
                      variant="contained"
                      onClick={() => handleApplyCompanyMatch(candidate)}
                      sx={{ ...PRIMARY_BUTTON_SX, whiteSpace: "nowrap" }}
                    >
                      Populate Empty Fields
                    </Button>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid rgba(0,51,108,0.10)" }}>
          <Button onClick={() => setMatchDialogOpen(false)} sx={{ color: BRAND_BLUE, fontWeight: 800 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {!isNew && (
        <ProspectingCompanyContactsModal
          open={contactsModalOpen}
          onClose={() => setContactsModalOpen(false)}
          companyId={id}
          companyName={company.name}
          onContactsChanged={() => setContactsRefreshKey((c) => c + 1)}
        />
      )}
    </Box>
  );
}

export default ProspectingCompanyDetails;
